import { useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import {
  Button,
  Drawer,
  Grid,
  Text,
  ReactSelectController,
  DateController,
  RadioController,
  NumberController,
  CheckboxController,
  Icons,
  DialogModal,
} from "@pushpress/shared-components";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import { DrawerPanel } from "../../../../components/common/DrawerPanel";
import { useAssignPlan } from "../../api/migrationAssistantApi.hooks";
import {
  AssignPlanFormType,
  MemberDataType,
  PaymentTypes,
  PlanDiscountDataType,
  PlanTypes,
} from "../../types";
import {
  formatDateForPicker,
  getDiscountOption,
  getDiscountOptions,
  getPlanOption,
  groupPlansByCategory,
} from "../../helpers";
import { defaultToastOptions, joinStrings } from "../../../../utils/misc";
import InfoCard from "../shared/InfoCard";
import { Plan } from "../../../../apis/subscriptionApi";
import UserAvatar from "../../../../components/common/UserAvatar/UserAvatar";
import { useAnalytics } from "../../../../components/providers/AnalyticsProvider";

type AddPlanSideDrawerType = {
  isDrawerOpen: boolean;
  setIsDrawerOpen: (isVisible: boolean) => void;
  setSelectedMember: (member: MemberDataType) => void;
  member: MemberDataType;
  plansData: Plan[];
  plansDiscountsData: PlanDiscountDataType["data"];
};

export default function AddPlanSideDrawer({
  isDrawerOpen,
  setIsDrawerOpen,
  setSelectedMember,
  member,
  plansData,
  plansDiscountsData,
}: AddPlanSideDrawerType) {
  const { trackEvent } = useAnalytics();
  const { mutate, isLoading } = useAssignPlan();
  const queryClient = useQueryClient();
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { isDirty },
  } = useForm<AssignPlanFormType>({
    mode: "onTouched",
    defaultValues: {
      plan: getPlanOption(plansData, member.planUuid),
      discount: getDiscountOption(plansDiscountsData, member.planDiscountUuid),
      planBillingDate: formatDateForPicker(member.planStartBilling),
      paymentType: member.planBillingType || PaymentTypes.STRIPE,
      planEndDate: member.planEndDate,
      planRemainingSessions: member.planRemainingSessions?.toString() || "",
    },
  });

  const [
    plan,
    discount,
    planBillingDate,
    paymentType,
    planRemainingSessions,
    planEndDate,
    punchcardExpires,
  ] = useWatch({
    control,
    name: [
      "plan",
      "discount",
      "planBillingDate",
      "paymentType",
      "planRemainingSessions",
      "planEndDate",
      "punchcardExpires",
    ],
  });

  function Save() {
    mutate(
      {
        id: member.id,
        planUuid: plan.value,
        planDiscountUuid: discount?.value ?? "",
        planStartBilling:
          plan.category === PlanTypes.RECURRING ? planBillingDate : null,
        planBillingType:
          plan.category === PlanTypes.RECURRING ? paymentType : "",
        needPlan: true,
        planRemainingSessions:
          plan.category === PlanTypes.PUNCH_CARD
            ? Number(planRemainingSessions)
            : null,
        planEndDate:
          plan.category === PlanTypes.NON_RECURRING ||
          (plan.category === PlanTypes.PUNCH_CARD && punchcardExpires)
            ? planEndDate
            : null,
      },
      {
        onSuccess: () => {
          toast.success("Plan saved", defaultToastOptions);
          queryClient.invalidateQueries({
            queryKey: ["fetchMigratingMembers"],
          });
          setIsDrawerOpen(false);
          setSelectedMember(null);
        },
        onError: (error) => {
          toast.error("There was an error saving plan", defaultToastOptions);
          trackEvent("Migration Assistant Error", {
            error: "ma_patch_api_add_plan_error",
            api_error: JSON.stringify(error),
          });
        },
      },
    );
  }

  function handleSave() {
    handleSubmit(Save)();
  }

  function handleClose() {
    if (isDirty) {
      setIsConfirmModalOpen(true);
    } else {
      setIsDrawerOpen(false);
      setSelectedMember(null);
    }
  }

  const groupedPlanOptions = groupPlansByCategory(plansData);
  const discountOptions = getDiscountOptions(plansDiscountsData);

  const showRecurringPlanFields =
    plan?.category === PlanTypes.RECURRING || !plan?.category;
  const showNonRecurringPlanFields = plan?.category === PlanTypes.NON_RECURRING;
  const showPuchcardPlanFields = plan?.category === PlanTypes.PUNCH_CARD;

  return (
    <Drawer
      visible={isDrawerOpen}
      anchor="right"
      width="400px"
      disableClickAway
      disableEscapeKeyDown
    >
      <DrawerPanel
        title="Add plan"
        saving={false}
        onClose={handleClose}
        loading={isLoading}
      >
        <Grid container alignItems="center" className="mt-1 mb-4">
          <Grid item>
            <UserAvatar size="medium" data={member} />
          </Grid>
          <Grid item>
            <Grid item>
              <Text variant="body-lg" color="wolf-800">
                {joinStrings(member.firstName, member.lastName)}
              </Text>
            </Grid>
            <Grid item>
              <Text variant="body-lg" color="wolf-600">
                {member.email}
              </Text>
            </Grid>
          </Grid>
        </Grid>
        <form onSubmit={handleSubmit(handleSave)}>
          <Grid>
            <ReactSelectController
              label="Plan"
              control={control}
              name="plan"
              required
              options={groupedPlanOptions}
              enableOptionColor
              placeholder="— Select —"
            />
          </Grid>
          {showRecurringPlanFields && (
            <>
              <Grid item marginY={2}>
                <ReactSelectController
                  label="Discount"
                  control={control}
                  name="discount"
                  placeholder="— Add —"
                  hideDropdownIndicator
                  options={discountOptions}
                  isClearable
                />
              </Grid>
              <Grid item>
                <DateController
                  label="Start billing"
                  control={control}
                  name="planBillingDate"
                  required
                  startIcon={Icons.Classes}
                  helperText="When do you want this member to receive their first bill in PushPress?"
                />
              </Grid>
              <Grid item marginY={2}>
                <RadioController
                  label=""
                  control={control}
                  name="paymentType"
                  options={[
                    {
                      label: "Bank or card",
                      value: PaymentTypes.STRIPE,
                    },
                    {
                      label: "Cash",
                      value: PaymentTypes.CASH,
                    },
                    {
                      label: "Comp",
                      value: PaymentTypes.COMP,
                    },
                  ]}
                />
              </Grid>
            </>
          )}
          {showNonRecurringPlanFields && (
            <>
              <Grid item marginY={3}>
                <DateController
                  label="Plan end date"
                  control={control}
                  name="planEndDate"
                  required
                  startIcon={Icons.Classes}
                />
              </Grid>
              <Grid item marginY={4}>
                <InfoCard />
              </Grid>
            </>
          )}
          {showPuchcardPlanFields && (
            <>
              <Grid item marginY={2}>
                <NumberController
                  label="Remaining sessions"
                  control={control}
                  name="planRemainingSessions"
                  required
                  min={1}
                  max={plan?.limitCheckins ? plan?.limitCheckinsQuantity : undefined}
                />
              </Grid>
              <Grid item>
                <CheckboxController
                  label="Punchcard expires"
                  control={control}
                  name="punchcardExpires"
                />
              </Grid>
              {punchcardExpires && (
                <>
                  <Grid item marginY={3}>
                    <DateController
                      label="Expiration date"
                      control={control}
                      name="planEndDate"
                      required
                      startIcon={Icons.Classes}
                    />
                  </Grid>
                </>
              )}
              <Grid item marginY={5}>
                <InfoCard />
              </Grid>
            </>
          )}
        </form>
        <Grid item>
          <Button
            size="large"
            fullWidth
            onClick={handleSave}
            text="Save"
            type="submit"
          />
        </Grid>
        <DialogModal
          title="Discard unsaved changes?"
          visible={isConfirmModalOpen}
          primaryBtnTitle="Discard"
          secondaryBtnTitle="Go back"
          primaryHandler={() => {
            setIsDrawerOpen(false);
            setSelectedMember(null);
          }}
          secondaryHandler={() => setIsConfirmModalOpen(false)}
          closeModal={() => setIsConfirmModalOpen(false)}
        />
      </DrawerPanel>
    </Drawer>
  );
}
